// PROMPT 150: perfil de WhatsApp Business de una marca (lo que ve el cliente). Solo para los numeros que
// atiende la plataforma: si el numero tambien esta en el celular, Meta no deja cambiarlo desde aca.
import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Store } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const MAX_FOTO = 5 * 1024 * 1024;
const ESTADOS_NOMBRE = {
  APPROVED: "aprobado",
  AVAILABLE_WITHOUT_REVIEW: "disponible",
  PENDING_REVIEW: "en revisión de Meta",
  DECLINED: "rechazado por Meta",
  EXPIRED: "vencido",
};
const AVISO_COEXISTENCIA =
  "Este número también está en la app del celular: el perfil se cambia desde la app (Meta no deja cambiarlo desde acá).";

function aFormulario(p) {
  const webs = (p && p.websites) || [];
  return {
    about: (p && p.about) || "",
    address: (p && p.address) || "",
    description: (p && p.description) || "",
    email: (p && p.email) || "",
    web1: webs[0] || "",
    web2: webs[1] || "",
    vertical: (p && p.vertical) || "",
  };
}

// Solo lo que cambio: lo que no se toca no se manda.
function cambiosDe(original, actual) {
  const cambios = {};
  ["about", "address", "description", "email"].forEach((k) => {
    if (actual[k].trim() !== original[k]) cambios[k] = actual[k].trim();
  });
  if (actual.vertical && actual.vertical !== original.vertical) cambios.vertical = actual.vertical;
  const nuevas = [actual.web1, actual.web2].map((w) => w.trim()).filter(Boolean);
  const viejas = [original.web1, original.web2].filter(Boolean);
  if (nuevas.join(" ") !== viejas.join(" ")) cambios.websites = nuevas;
  return cambios;
}

function Campo({ id, titulo, ayuda, children }) {
  return (
    <div>
      <Label htmlFor={id} className="text-xs text-slate-600">
        {titulo}
      </Label>
      <div className="mt-1">{children}</div>
      {ayuda ? <p className="mt-0.5 text-[11px] text-slate-500">{ayuda}</p> : null}
    </div>
  );
}

export function PerfilWhatsAppDialog({ slug, abierto, onCerrar }) {
  const { isAdmin } = useAuth();
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [datos, setDatos] = useState(null);
  const [original, setOriginal] = useState(null);
  const [form, setForm] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [subiendo, setSubiendo] = useState(false);
  const archivo = useRef(null);
  const base = `/whatsapp/canal/${encodeURIComponent(slug)}/perfil`;

  const cargar = useCallback(async () => {
    setCargando(true);
    setError("");
    try {
      const { data } = await api.get(base);
      const f = aFormulario(data.perfil);
      setDatos(data);
      setOriginal(f);
      setForm(f);
    } catch (e) {
      setDatos(null);
      setError(formatApiError(e.response?.data?.detail));
    } finally {
      setCargando(false);
    }
  }, [base]);

  useEffect(() => {
    if (abierto) cargar();
  }, [abierto, cargar]);

  const cambios = original && form ? cambiosDe(original, form) : {};
  const hayCambios = Object.keys(cambios).length > 0;
  const soloLectura = !isAdmin || Boolean(datos && datos.coexistencia);
  const poner = (campo, valor) => setForm((f) => ({ ...f, [campo]: valor }));

  const guardar = async () => {
    if (!hayCambios) return;
    setGuardando(true);
    try {
      await api.put(base, cambios);
      toast.success("Perfil actualizado.");
      await cargar();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setGuardando(false);
    }
  };

  const elegirFoto = async (e) => {
    const elegido = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!elegido) return;
    if (!["image/jpeg", "image/png"].includes(elegido.type)) {
      toast.error("La foto tiene que ser JPG o PNG.");
      return;
    }
    if (elegido.size > MAX_FOTO) {
      toast.error("La foto pesa más de 5 MB.");
      return;
    }
    setSubiendo(true);
    try {
      const fd = new FormData();
      fd.append("archivo", elegido);
      await api.post(`${base}/foto`, fd);
      toast.success("Foto cambiada.");
      await cargar();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubiendo(false);
    }
  };

  const estadoNombre = datos && datos.estado_nombre ? ESTADOS_NOMBRE[datos.estado_nombre] || datos.estado_nombre : "";

  return (
    <Dialog open={abierto} onOpenChange={(v) => !v && onCerrar()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="wa-perfil-dialogo">
        <DialogHeader>
          <DialogTitle>Perfil de WhatsApp</DialogTitle>
          <DialogDescription>Lo que ve el cliente cuando abre el perfil del negocio.</DialogDescription>
        </DialogHeader>
        {cargando ? (
          <div className="flex items-center gap-2 py-6 text-sm text-slate-500" data-testid="wa-perfil-cargando">
            <Loader2 className="h-4 w-4 animate-spin" /> Consultando a Meta…
          </div>
        ) : error ? (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800" role="alert" data-testid="wa-perfil-error">
            {error}
          </div>
        ) : datos && form ? (
          <div className="space-y-3">
            {datos.coexistencia ? (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="wa-perfil-coexistencia">
                {AVISO_COEXISTENCIA}
              </div>
            ) : !isAdmin ? (
              <p className="text-xs text-slate-500">Solo un administrador puede cambiar el perfil.</p>
            ) : null}
            <div className="flex items-center gap-3">
              {datos.perfil.foto ? (
                <img src={datos.perfil.foto} alt="Foto actual del perfil" className="h-16 w-16 shrink-0 rounded-full object-cover" data-testid="wa-perfil-foto" />
              ) : (
                <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                  <Store className="h-7 w-7" />
                </div>
              )}
              <div className="min-w-0">
                <input ref={archivo} type="file" accept="image/jpeg,image/png" className="hidden" onChange={elegirFoto} data-testid="wa-perfil-foto-input" />
                <Button
                  size="sm"
                  variant="outline"
                  disabled={soloLectura || subiendo}
                  onClick={() => archivo.current && archivo.current.click()}
                  className="gap-1.5"
                  data-testid="wa-perfil-foto-cambiar"
                >
                  {subiendo ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                  Cambiar foto
                </Button>
                <p className="mt-0.5 text-[11px] text-slate-500">JPG o PNG, hasta 5 MB.</p>
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Nombre</Label>
              <p className="mt-1 text-sm text-slate-800" data-testid="wa-perfil-nombre">
                {datos.nombre || "Sin nombre aprobado"}
                {estadoNombre ? <span className="text-slate-500"> ({estadoNombre})</span> : null}
              </p>
              <p className="mt-0.5 text-[11px] text-slate-500">
                El nombre se cambia desde WhatsApp Manager: Meta lo revisa y después hay que volver a registrar el número.
              </p>
            </div>
            <Campo id="wa-perfil-about" titulo={`Estado (${form.about.length}/139)`} ayuda="El texto corto que aparece debajo del nombre.">
              <Input id="wa-perfil-about" value={form.about} maxLength={139} disabled={soloLectura} onChange={(e) => poner("about", e.target.value)} className="text-base lg:text-sm" data-testid="wa-perfil-about" />
            </Campo>
            <Campo id="wa-perfil-description" titulo={`Descripción (${form.description.length}/512)`}>
              <Textarea id="wa-perfil-description" value={form.description} maxLength={512} disabled={soloLectura} onChange={(e) => poner("description", e.target.value)} className="min-h-[72px] text-base lg:text-sm" data-testid="wa-perfil-description" />
            </Campo>
            <Campo id="wa-perfil-address" titulo={`Dirección (${form.address.length}/256)`}>
              <Input id="wa-perfil-address" value={form.address} maxLength={256} disabled={soloLectura} onChange={(e) => poner("address", e.target.value)} className="text-base lg:text-sm" data-testid="wa-perfil-address" />
            </Campo>
            <Campo id="wa-perfil-email" titulo="Email">
              <Input id="wa-perfil-email" type="email" value={form.email} maxLength={128} disabled={soloLectura} onChange={(e) => poner("email", e.target.value)} className="text-base lg:text-sm" data-testid="wa-perfil-email" />
            </Campo>
            <Campo id="wa-perfil-web1" titulo="Sitios web" ayuda="Hasta 2, con http:// o https://.">
              <div className="space-y-1.5">
                <Input id="wa-perfil-web1" value={form.web1} maxLength={256} disabled={soloLectura} onChange={(e) => poner("web1", e.target.value)} placeholder="https://" className="text-base lg:text-sm" data-testid="wa-perfil-web1" />
                <Input id="wa-perfil-web2" value={form.web2} maxLength={256} disabled={soloLectura} onChange={(e) => poner("web2", e.target.value)} placeholder="https://" aria-label="Segundo sitio web" className="text-base lg:text-sm" data-testid="wa-perfil-web2" />
              </div>
            </Campo>
            <Campo id="wa-perfil-vertical" titulo="Rubro">
              <select
                id="wa-perfil-vertical"
                value={form.vertical}
                disabled={soloLectura}
                onChange={(e) => poner("vertical", e.target.value)}
                className="h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-base text-slate-700 lg:text-sm"
                data-testid="wa-perfil-vertical"
              >
                <option value="">Sin definir</option>
                {(datos.verticales || []).map((v) => (
                  <option key={v.valor} value={v.valor}>
                    {v.nombre}
                  </option>
                ))}
              </select>
            </Campo>
            {!soloLectura ? (
              <div className="flex justify-end pt-1">
                <Button onClick={guardar} disabled={guardando || !hayCambios} data-testid="wa-perfil-guardar">
                  {guardando ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
                  Guardar el perfil
                </Button>
              </div>
            ) : null}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

// El boton que va en la tarjeta de cada marca del Canal.
export function BotonPerfilWhatsApp({ slug, deshabilitado }) {
  const [abierto, setAbierto] = useState(false);
  return (
    <>
      <Button
        size="sm"
        variant="outline"
        onClick={() => setAbierto(true)}
        disabled={deshabilitado}
        title={deshabilitado ? "Primero cargá y guardá el número" : "Ver y cambiar el perfil que ve el cliente"}
        className="gap-1.5"
        data-testid={`wa-perfil-abrir-${slug}`}
      >
        <Store className="h-3.5 w-3.5" />
        Perfil de WhatsApp
      </Button>
      {abierto ? <PerfilWhatsAppDialog slug={slug} abierto={abierto} onCerrar={() => setAbierto(false)} /> : null}
    </>
  );
}
